import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * A5 landscape poster for noticeboards in SARCs, refuges, GP surgeries,
 * university welfare offices, ISVA / IDVA clinics etc.
 *
 * Rendered inline in the DOM but hidden from the screen. Only becomes
 * visible when the user triggers the "Print A5 poster" flow, which adds
 * the `tf-print-poster` class to <body> and calls window.print(). The
 * dedicated print stylesheet in index.css isolates this poster and sets
 * @page size to landscape A5.
 *
 * Kept intentionally text-light so it reads clearly from a metre away.
 */
export default function PosterA5({ url = 'https://thefullimpact.org/?src=qr' }) {
  const displayUrl = url.replace(/^https?:\/\//, '').replace(/\?src=.*$/, '');
  return (
    <aside
      className="tf-a5-poster"
      aria-hidden="true"
      data-testid="a5-poster"
    >
      <div className="tf-a5-inner">
        {/* Left column — words */}
        <div className="tf-a5-left">
          <p className="tf-a5-eyebrow">THE FULL IMPACT</p>
          <h1 className="tf-a5-title">
            You are believed.<br />You are not alone.
          </h1>
          <p className="tf-a5-lede">
            A calm, anonymous UK guide for survivors of{' '}
            <strong>sexual violence</strong> and{' '}
            <strong>domestic abuse</strong>. Read privately in your browser —
            free, no sign-up, no tracking.
          </p>

          <ul className="tf-a5-list">
            <li>The judicial process, explained gently</li>
            <li>Your UK rights &amp; how ISVAs / IDVAs help</li>
            <li>Trauma, grounding tools &amp; a private journal</li>
            <li>Specialist helplines across England &amp; Wales</li>
          </ul>

          <div className="tf-a5-helplines">
            <p className="tf-a5-helplines-title">In immediate danger — call 999</p>
            <p className="tf-a5-helpline">Rape Crisis 24/7 · 0808 802 9999</p>
            <p className="tf-a5-helpline">Refuge (domestic abuse) 24/7 · 0808 2000 247</p>
            <p className="tf-a5-helpline">Samaritans 24/7 · 116 123</p>
            <p className="tf-a5-helpline">Live Fear Free (Wales) · 0808 80 10 800</p>
          </div>
        </div>

        {/* Right column — QR + URL */}
        <div className="tf-a5-right">
          <div className="tf-a5-qr-wrap">
            <QRCodeSVG
              value={url}
              size={280}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#3F1F5B"
            />
          </div>
          <p className="tf-a5-scan-label">SCAN TO OPEN</p>
          <p className="tf-a5-url">{displayUrl}</p>
          <p className="tf-a5-badge">FREE · ANONYMOUS · TRAUMA-INFORMED</p>
        </div>
      </div>

      <p className="tf-a5-footer">
        © {new Date().getFullYear()} Angela Moreton · The Full Impact™ ·
        Content sourced from Rape Crisis, CPS, NHS, gov.uk. Not legal advice.
      </p>
    </aside>
  );
}
